/**
 * SE2 · Sticky buy bar (2026-09-18; state, timing and every width 2026-09-29). Markup:
 * snippets/se2-sticky-buy.liquid. Styles: the .se2-stickybuy rules in assets/se2-ethereal.css.
 *
 * Four jobs, all of them deliberately small:
 *   1. WHEN. Below 990 the bar shows while neither the product's head (name and price) nor the real add button
 *      is on screen: it stays off the first screen, arrives as soon as the price scrolls away, steps aside while
 *      the real button is in view and returns once it has passed (DPDP-M-03, FPDP-M-06). From 990 the buy column
 *      is sticky, so the bar shows only once the column's add button has left the viewport upward (DPDP-D-17).
 *      Both are IntersectionObservers on the viewport, so they hold whether the document or .page-wrapper (990
 *      and up, assets/base.css) is the scroller.
 *   2. WHAT IT SAYS. It mirrors the real button (DPDP-M-01, FPDP-M-01): its label, its disabled state (the
 *      secondary pill, no price) and its in-button price. A sold-out selection with a back-in-stock block on the
 *      page reads "Email me when it's back" ("Email me" wherever the full words would clip the product's name)
 *      and leads there. On a fragrance oil it reads "Choose size" until the real button has been on screen once,
 *      so nobody adds a pre-selected size or plan without having seen them.
 *   3. WHAT IT DOES. It adds to cart by clicking the PDP's own add button: same form, same variant, same selling
 *      plan, same quantity, same error handling. When that is not possible it scrolls to the control that
 *      explains why (the sizes, the colours, the restock form, opened at its email field) and puts focus there.
 *   4. THE VARIANT LINE. The selected colour with its finish dot, or the size; "120 mL · every 30 days" while a
 *      Subscribe & save plan is checked. On a SCENTPRO with the kit offer live, the top bar's offer line
 *      (DPDP-D-17) shows only while the bar adds: a sold-out colour has no free bottle to offer.
 */

const SE2_STICKY_TOP_MQ = '(min-width: 990px)';
const SE2_STICKY_REDUCE_MQ = '(prefers-reduced-motion: reduce)';
/* The fragrance PDP's head is two nodes (title group, then the price row); the diffuser PDP's is one. */
const SE2_STICKY_HEADS = ['[id^="se2-dpdp-head-"]', '.se2-fpdp3__title-group, .se2-fpdp3__price-row'];
const SE2_STICKY_COPY = {
  choose: 'Choose size',
  restock: "Email me when it's back",
  // The same action in two words, used only while the full words would push the product's name into an ellipsis
  // (a 390px phone on a sold-out Tower or Mini). The button then carries the full words as its accessible name,
  // which still begins with the visible ones (WCAG 2.5.3).
  restockShort: 'Email me',
};

class Se2StickyBuy extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  #io;
  /** @type {MutationObserver | undefined} */
  #mo;
  /** @type {AbortController | undefined} */
  #abort;
  /** @type {HTMLElement | null} */
  #target = null;
  /** @type {Element | null} */
  #watch = null;
  /** @type {Element[]} */
  #heads = [];
  /** @type {Map<Element, { in: boolean, top: number }>} */
  #seen = new Map();
  /** The real add button has been on screen at least once in this page view. */
  #atcSeen = false;
  /** Fragrance oils: the buy box carries a purchase-options slot. */
  #isOil = false;
  /** @type {'add' | 'choose' | 'restock' | 'soldout'} */
  #mode = 'add';
  #raf = 0;
  /** @type {MediaQueryList | undefined} */
  #top;
  /** @type {Record<string, string>} */
  #colours = {};
  /** Restock mode reads "Email me" because the full words would clip the name (the name outranks the label). */
  #short = false;
  /** The viewport width #short was decided at; 0 until decided, and again after leaving restock mode. */
  #fitWidth = 0;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    const target = document.querySelector(this.dataset.target || '');
    // Without a buy box there is nothing to stand in for, so the bar would be a dead end: stay hidden.
    if (!(target instanceof HTMLElement)) return;
    this.#target = target;
    // A fragrance oil: the buy box carries the purchase-options slot. That slot is left out while the selected
    // size is sold out, so the fragrance PDP's own buy box counts too.
    this.#isOil =
      !!target.querySelector('[data-se2-po-slot], [data-se2-plan-slot]') ||
      target.matches('[data-testid="se2-fpdp3-purchase"]');

    try {
      this.#colours = JSON.parse(this.dataset.se2Colours || '{}') || {};
    } catch {
      this.#colours = {};
    }

    for (const selector of SE2_STICKY_HEADS) {
      const found = Array.from(document.querySelectorAll(selector)).filter((node) => !target.contains(node));
      if (found.length) {
        this.#heads = selector.startsWith('[id^=') ? found.slice(0, 1) : found;
        break;
      }
    }

    this.addEventListener('click', this.#onClick, { signal });

    this.#top = window.matchMedia(SE2_STICKY_TOP_MQ);
    this.#top.addEventListener('change', this.#update, { signal });

    // A phone's resize fires on every scroll as the browser chrome collapses; only a change of WIDTH can change
    // whether the name fits beside the label, and #fit ignores the rest. Web fonts arriving late re-measure once.
    window.addEventListener('resize', () => this.#fit(), { signal, passive: true });
    document.fonts?.ready.then(() => {
      this.#fitWidth = 0;
      this.#fit();
    });

    this.#io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          this.#seen.set(entry.target, { in: entry.isIntersecting, top: entry.boundingClientRect.top });
          if (entry.target === this.#watch && entry.isIntersecting) this.#atcSeen = true;
        }
        this.#update();
      },
      { threshold: 0 }
    );
    this.#observeWatch();
    for (const head of this.#heads) this.#io.observe(head);

    this.#sync();
    // The whole buy box, not the price span: assets/se2-pdp-purchase.js replaces spans and slots on a variant
    // change, Horizon toggles the button's disabled state, and a restock block may appear after the form. The
    // box outlives every swap; #sync re-reads whatever is current, once per frame.
    this.#mo = new MutationObserver(this.#queueSync);
    this.#mo.observe(target, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['disabled', 'hidden', 'checked', 'aria-disabled'],
    });
    // Radio and plan choices change properties, not attributes, so listen for the change itself too.
    document.addEventListener('change', this.#queueSync, { signal });
  }

  disconnectedCallback() {
    this.#io?.disconnect();
    this.#mo?.disconnect();
    this.#abort?.abort();
    if (this.#raf) cancelAnimationFrame(this.#raf);
  }

  /** The add button's own wrapper; .se2-buy__atc survives every variant swap. The column is the fallback. */
  #observeWatch() {
    const target = this.#target;
    if (!target || !this.#io) return;
    if (this.#watch && this.#watch.isConnected) return;
    if (this.#watch) {
      this.#io.unobserve(this.#watch);
      this.#seen.delete(this.#watch);
    }
    this.#watch = target.querySelector('.se2-buy__atc') || target;
    this.#io.observe(this.#watch);
  }

  #update = () => {
    const atc = this.#watch ? this.#seen.get(this.#watch) : undefined;
    if (!atc) return;
    const atcPast = !atc.in && atc.top < 0;
    let hidden;
    if (this.#top?.matches || this.#heads.length === 0) {
      hidden = atc.in || !atcPast;
    } else {
      hidden = atc.in || this.#heads.some((head) => this.#seen.get(head)?.in);
    }
    if (hidden !== this.hidden) {
      this.hidden = hidden;
      this.classList.toggle('is-shown', !hidden);
    }
    this.#sync();
  };

  #queueSync = () => {
    if (this.#raf) return;
    this.#raf = requestAnimationFrame(() => {
      this.#raf = 0;
      this.#sync();
    });
  };

  /** The real add button in the buy box. */
  #addButton() {
    const add = this.#target?.querySelector('button[name="add"]');
    return add instanceof HTMLButtonElement ? add : null;
  }

  #restock() {
    return this.#target?.querySelector('[data-se2-restock]') || document.querySelector('[data-se2-restock]');
  }

  /**
   * The real button's words without its price, its separator and its "added" burst.
   * @param {HTMLButtonElement} add
   */
  #labelOf(add) {
    const own = add.querySelector('[data-se2-atc-label]');
    if (own) return (own.textContent || '').replace(/\s+/g, ' ').trim();
    const copy = /** @type {HTMLElement} */ (add.cloneNode(true));
    for (const extra of copy.querySelectorAll(
      '[data-se2-atc-price], .se2-buy__atc-sep, .add-to-cart__added, .svg-wrapper, [aria-hidden="true"]'
    )) {
      extra.remove();
    }
    return (copy.textContent || '').replace(/\s+/g, ' ').trim();
  }

  #sync = () => {
    const target = this.#target;
    if (!target) return;
    this.#observeWatch();

    const add = this.#addButton();
    const disabled = !add || add.disabled;
    const restock = disabled ? this.#restock() : null;

    /** @type {'add' | 'choose' | 'restock' | 'soldout'} */
    let mode = 'add';
    if (this.#isOil && !this.#atcSeen) mode = 'choose';
    else if (!disabled) mode = 'add';
    else if (restock) mode = 'restock';
    else mode = 'soldout';
    this.#mode = mode;
    // Leaving restock mode forgets the short label, so the next sold-out selection starts from the full words.
    if (mode !== 'restock') {
      this.#short = false;
      this.#fitWidth = 0;
    }

    // label
    const label = this.querySelector('[data-se2-sticky-label]');
    if (label) {
      let text = '';
      if (mode === 'choose') text = SE2_STICKY_COPY.choose;
      else if (mode === 'restock') text = this.#short ? SE2_STICKY_COPY.restockShort : SE2_STICKY_COPY.restock;
      else if (add) text = this.#labelOf(add);
      if (text && label.textContent !== text) label.textContent = text;
    }

    // price: only while the bar really adds, and only when the real button has one
    const price = this.querySelector('[data-se2-sticky-price]');
    if (price instanceof HTMLElement) {
      const source = (this.#watch || target).querySelector('[data-se2-atc-price]');
      const figure = source ? (source.textContent || '').trim() : '';
      if (figure && price.textContent !== figure) price.textContent = figure;
      price.hidden = mode !== 'add' || figure === '';
    }

    // the kit offer line, printed by the Liquid only while the offer is live: never beside a sold-out choice
    const offer = this.querySelector('[data-se2-sticky-offer]');
    if (offer instanceof HTMLElement) offer.hidden = mode !== 'add';

    // state: the secondary pill whenever the bar does not add; announced as unavailable only when there is
    // truly nothing to do here but look at the choices (the restock and choose-size taps are live actions)
    const button = this.querySelector('[data-se2-sticky-add]');
    if (button instanceof HTMLElement) {
      button.classList.toggle('is-disabled', mode !== 'add');
      if (mode === 'soldout') button.setAttribute('aria-disabled', 'true');
      else button.removeAttribute('aria-disabled');
    }

    this.#syncVariant(mode);
    this.#applyShort();
    this.#fit();
  };

  /** The accessible name carries the full words only while the visible label is the short one. */
  #applyShort() {
    const button = this.querySelector('[data-se2-sticky-add]');
    if (!(button instanceof HTMLElement)) return;
    if (this.#mode === 'restock' && this.#short) button.setAttribute('aria-label', SE2_STICKY_COPY.restock);
    else button.removeAttribute('aria-label');
  }

  /**
   * Restock mode only: "Email me when it's back" is the widest label the bar carries, and on a phone it pushed the
   * product's name into an ellipsis. The name outranks the label, so when the full words clip it the label becomes
   * "Email me". Decided once per viewport width, and only while the bar is shown, because display: none measures 0;
   * #update runs this (through #sync) as the bar appears, and the resize listener on a change of width.
   */
  #fit() {
    if (this.#mode !== 'restock' || this.hidden) return;
    if (this.#fitWidth === window.innerWidth) return;
    const name = this.querySelector('.se2-stickybuy__name');
    const label = this.querySelector('[data-se2-sticky-label]');
    if (!(name instanceof HTMLElement) || !label) return;
    this.#fitWidth = window.innerWidth;
    label.textContent = SE2_STICKY_COPY.restock;
    this.#short = name.scrollWidth > name.clientWidth;
    if (this.#short) label.textContent = SE2_STICKY_COPY.restockShort;
    this.#applyShort();
  }

  /** @param {'add' | 'choose' | 'restock' | 'soldout'} mode */
  #syncVariant(mode) {
    const line = this.querySelector('[data-se2-sticky-variant]');
    const text = this.querySelector('[data-se2-sticky-variant-text]');
    const dot = this.querySelector('[data-se2-sticky-dot]');
    if (!(line instanceof HTMLElement) || !text) return;

    // "Choose size" names no size: the pre-selected one is exactly what the shopper has not seen yet
    if (mode === 'choose') {
      line.hidden = true;
      return;
    }

    const scope = this.#target?.querySelector('variant-picker') || this.#target || document;
    /** @type {{ name: string, value: string }[]} */
    const picks = [];
    // Only the variant picker's own inputs (they carry data-option-name), never the purchase-option radios.
    for (const input of scope.querySelectorAll('input[data-option-name]:checked')) {
      if (!(input instanceof HTMLInputElement)) continue;
      picks.push({ name: input.dataset.optionName || '', value: (input.value || '').trim() });
    }
    for (const option of scope.querySelectorAll('select option[data-option-name]:checked')) {
      if (!(option instanceof HTMLOptionElement)) continue;
      picks.push({ name: option.dataset.optionName || '', value: (option.value || option.textContent || '').trim() });
    }
    // Without a picker (a single-variant product) the Liquid line stands.
    if (!picks.length) return;

    const colour = picks.find((pick) => /^colou?r$/i.test(pick.name.trim()));
    let value = colour ? colour.value : picks[0]?.value || '';

    const plan = this.#target?.querySelector('[data-se2-plan-radio]:checked');
    const every = (this.#target?.querySelector('[data-se2-plan-current]')?.textContent || '').trim();
    // the plan's cadence only while the bar really adds that plan; a sold-out size has nothing to deliver
    if (value && plan && every && mode === 'add') value = `${value} · every ${every}`;

    if (text.textContent !== value) text.textContent = value;
    line.hidden = value === '';

    if (dot instanceof HTMLElement) {
      dot.hidden = !colour;
      if (colour) {
        const handle = colour.value
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
        const css = this.#colours[colour.value] || `var(--se2-finish-${handle}, var(--se2-line))`;
        dot.style.setProperty('--se2-stickybuy-dot', css);
      }
    }
  }

  /**
   * Scoped to the add button so the name and variant stay inert text.
   * @param {Event} event
   */
  #onClick = (event) => {
    const hit = event.target instanceof Element ? event.target.closest('[data-se2-sticky-add]') : null;
    if (!hit) return;
    const target = this.#target;
    if (!target) return;
    this.#sync();

    // Click the real button. product-form.js owns the request, the live region and the error path from here.
    const add = this.#addButton();
    if (this.#mode === 'add' && add && !add.disabled) {
      add.click();
      return;
    }

    if (this.#mode === 'restock') {
      const restock = this.#restock();
      if (restock instanceof HTMLElement) {
        // The bar promised the email form, so the tap opens it and lands in the email field rather than on its
        // closed summary. The in-stock alternative ("Switch to…", "In stock now…") stays above it, in view. After a
        // request has been sent the block holds only its thank-you line, and focus falls back to the block.
        const details = restock.querySelector('details.se2-restock__notify');
        if (details instanceof HTMLDetailsElement && !details.open) details.open = true;
        const focusable =
          (details instanceof HTMLDetailsElement && details.open ? details.querySelector('input[type="email"]') : null) ||
          restock.querySelector('summary') ||
          restock.querySelector('input:not([type="hidden"]), button, a[href]') ||
          restock;
        this.#reveal(restock, focusable);
        return;
      }
    }

    // Choose size, or sold out with nowhere else to go: the choices themselves, focus on the checked one.
    const fieldset = target.querySelector('variant-picker fieldset, .variant-option');
    const picker = fieldset || target.querySelector('variant-picker') || target;
    const input =
      picker.querySelector('input:checked') || picker.querySelector('input:not([type="hidden"]), select') || null;
    this.#reveal(picker, input);
  };

  /**
   * @param {Element} node
   * @param {Element | null} focusable
   */
  #reveal(node, focusable) {
    const reduce = window.matchMedia(SE2_STICKY_REDUCE_MQ).matches;
    node.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
    if (!(focusable instanceof HTMLElement)) return;
    if (!focusable.matches('summary, a[href], button, input, select, textarea, [tabindex]')) {
      focusable.setAttribute('tabindex', '-1');
    }
    const go = () => focusable.focus({ preventScroll: true });
    if (reduce) go();
    else window.setTimeout(go, 420);
  }
}

if (!customElements.get('se2-sticky-buy')) {
  customElements.define('se2-sticky-buy', Se2StickyBuy);
}
